import { useRef, useCallback, useEffect, useSyncExternalStore } from "react";
import { href } from "./lib/utils";
import { useChatFlagsStore } from "./stores/chatFlagsStore";
import {
	adaptChat,
	type Chat,
	type Message,
	type RawClient,
} from "./lib/xmpp";

export { href };

export const ios = navigator.userAgent.match(/(iPad|iPhone|iPod)/g);
export const webkit =
	navigator.userAgent.match(/AppleWebKit/) &&
	!navigator.userAgent.match(/Chrom/);

export function useEffectEvent<Args extends unknown[], R>(
	fn: (...args: Args) => R,
): (...args: Args) => R {
	const ref = useRef(fn);
	useEffect(() => { ref.current = fn; });
	return useCallback((...args: Args) => { return ref.current(...args); }, []);
}

// REQ-CN-001.B.2: useChatExtras and its supporting types (Attachment,
// ChatExtras, emptyExtra) were removed in this slice. They were exported
// but had zero consumers anywhere in src/, confirmed by:
//   grep -rln "useChatExtras\|getChatExtras\|setChatExtras\|ChatExtras" src/
// returning only util.ts (the definition) and util.test.ts (a comment).
// The cleanest path forward was to delete the dead code rather than
// adapter-wrap borogove.ChatAttachment / CommandSession / Form just to
// preserve unused infrastructure. When per-chat drafts / attachments /
// command sessions land for real, the storage layer is a fresh design
// problem — re-introducing this hook prematurely would lock in a shape
// none of today's callers actually validated.

// ---------------------------------------------------------------------------
// showNotification — surface a desktop / SW notification for an inbound message.
//
// Tracks REQ-CN-001.B.1: now takes a typed Message from @/lib/xmpp, so
// callers must adapt the raw borogove ChatMessage at the boundary.
// ---------------------------------------------------------------------------

export const showNotification = async (
	accountId: string,
	message: Message,
): Promise<Notification | undefined> => {
	if (!Notification?.permission) return undefined;

	// Respect per-chat mute / notification-mode flags (REQ-CN-002 chatFlagsStore).
	const chatIdStr = message.chatId;
	const flags = useChatFlagsStore.getState();
	if (flags.isMuted(accountId, chatIdStr)) return undefined;
	if (flags.getNotifMode(accountId, chatIdStr) === "none") return undefined;

	const tag = accountId + ">" + chatIdStr;
	const messageId = message.serverId ?? message.localId;

	if (navigator.serviceWorker) {
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		const notifs = await serviceWorkerRegistration.getNotifications({ tag: tag });
		for (const notif of notifs) {
			if (notif.data.messageId === messageId) return undefined;
		}
	}

	const title = "Message from " + chatIdStr;
	const options: NotificationOptions = {
		body: message.text.length > 0 ? message.text : undefined,
		tag,
		// Standard NotificationOptions extras supported by browser SW:
		// renotify / vibrate are not in TS lib types but are honored at runtime.
		// We pass them via cast at the lone point of contact.
		...({ renotify: true, vibrate: [50, 50, 50] } as Record<string, unknown>),
		timestamp: message.timestamp,
		data: {
			accountId,
			chatId: chatIdStr,
			messageId,
			timestamp: message.timestamp,
		},
	} as NotificationOptions;

	if (navigator.serviceWorker) {
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		await serviceWorkerRegistration.showNotification(title, options);
		return undefined;
	}

	const notif = new Notification(title, options);
	notif.addEventListener("click", () => {
		history.pushState(
			null,
			"",
			href("/accounts/:accountId/chats/:chatId", {
				accountId,
				chatId: chatIdStr,
			}) + `#message:${messageId ?? ""}`,
		);
		window.dispatchEvent(new PopStateEvent("popstate"));
	});
	return notif;
};

// ---------------------------------------------------------------------------
// badScript — heuristic for spam: text is "bad" if it contains letters
// outside the user's expected scripts for every configured locale.
// Pure, deterministic, easily testable.
// ---------------------------------------------------------------------------

const negatedScriptRegex = {
	Latn: /(?=\p{L})\P{Script=Latin}/u,
	Cyrl: /(?=\p{L})\P{Script=Cyrillic}/u,
	Arab: /(?=\p{L})\P{Script=Arabic}/u,
	Hans: /(?=\p{L})\P{Script=Han}/u,
	Hant: /(?=\p{L})\P{Script=Han}/u,
	Jpan: /(?=\p{L})[^\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u,
	Kore: /(?=\p{L})[^\p{Script=Hangul}\p{Script=Han}]/u,
	Deva: /(?=\p{L})\P{Script=Devanagari}/u,
	Guru: /(?=\p{L})\P{Script=Gurmukhi}/u,
	Ethi: /(?=\p{L})\P{Script=Ethiopic}/u,
	Hebr: /(?=\p{L})\P{Script=Hebrew}/u,
	Grek: /(?=\p{L})\P{Script=Greek}/u,
};

export const badScript = (s: string): boolean => {
	for (const lang of navigator.languages) {
		const script = (new Intl.Locale(lang).maximize().script ?? "Latn") as keyof typeof negatedScriptRegex;
		if (!negatedScriptRegex[script].test(s)) return false;
	}
	return true;
};

// ---------------------------------------------------------------------------
// isInvite — pure: classify a chat as an invite / message-request given the
// user's spam-filter setting.
// ---------------------------------------------------------------------------

export const isInvite = (messageRequests: string, c: Chat): boolean => {
	if (c.uiState === "invited") return true;
	if (messageRequests === "strangers") return !c.isTrusted;
	if (messageRequests === "spam" && c.lastMessage !== undefined && c.lastMessage.text.length > 0) {
		if (c.isTrusted || c.isBookmarked) return false;
		return badScript(c.lastMessage.text);
	}
	return false;
};

// ---------------------------------------------------------------------------
// useChats — subscribe to a borogove Client's chats, returning the typed,
// adapter-wrapped Chat[]. Caches adapter results by raw chat reference so
// React's referential-equality checks stay efficient.
//
// Input is the opaque RawClient handle from @/lib/xmpp (borogove.Client under
// the alias). When REQ-CN-001.B.5+ migrates Chat.tsx and the auth flow,
// consumers can move to passing the adapted Client and this hook's input
// type tightens to match. Output is already the typed surface.
// ---------------------------------------------------------------------------

export const useChats = (client: RawClient | undefined): ReadonlyArray<Chat> => {
	const chatCache = useRef(new WeakMap<object, Chat>()).current;
	const chatsRef = useRef<Chat[] | null>(client !== undefined ? [] : []);

	useEffect(() => {
		chatsRef.current = client !== undefined ? null : [];
	}, [client]);

	return useSyncExternalStore(
		(callback) => {
			if (client === undefined) return () => {};

			const token1 = client.addChatsUpdatedListener((updated) => {
				for (const rawChat of updated) {
					chatCache.delete(rawChat as unknown as object);
				}
				chatsRef.current = null;
				callback();
			});

			// Re-render on any new message so unread counts stay live.
			// queueMicrotask defers until borogove finishes mutating the chat
			// object (it mutates synchronously but the listener fires before
			// mutation completes for some events).
			const messageReg = (client as { addChatMessageListener?: (cb: (m: unknown) => void) => unknown }).addChatMessageListener;
			let token2: unknown;
			if (typeof messageReg === "function") {
				token2 = messageReg.call(client, (msg: unknown) => {
					const msgChatId = (msg as { chatId?: () => string })?.chatId?.();
					if (msgChatId !== undefined) {
						const list = client.getChats() ?? [];
						const hit = list.find((c) => c.chatId === msgChatId);
						if (hit !== undefined) {
							chatCache.delete(hit as unknown as object);
						}
					}
					chatsRef.current = null;
					queueMicrotask(callback);
				});
			}

			return () => {
				client.removeEventListener(token1);
				if (token2 !== undefined) client.removeEventListener(token2);
			};
		},
		() => {
			if (chatsRef.current !== null) return chatsRef.current;
			const out: Chat[] = [];
			for (const raw of client?.getChats() ?? []) {
				const key = raw as unknown as object;
				let adapted = chatCache.get(key);
				if (adapted === undefined) {
					try {
						adapted = adaptChat(raw);
						chatCache.set(key, adapted);
					} catch {
						continue;
					}
				}
				out.push(adapted);
			}
			chatsRef.current = out;
			return chatsRef.current;
		},
	);
};
